import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShoppingCart, FileText, Wallet, Sparkles, TrendingUp } from "lucide-react";
import { useAuth, useHasPerm } from "@/context/AuthContext";
import PedidosPage from "@/pages/PedidosPage";
import RemisionesPage from "@/pages/RemisionesPage";
import PagosPage from "@/pages/PagosPage";
import DirectorPage from "@/pages/DirectorPage";
import ReportesPage from "@/pages/ReportesPage";

/**
 * Hub `/pedidos` — consolida todas las vistas relacionadas con la operación
 * de ventas para no saturar el sidebar:
 *   - Pedidos (todos los usuarios)
 *   - Remisiones OCR (director + `ventas.ver_todos`)
 *   - Pagos y Comprobantes (director + `pagos.aprobar` / `pagos.reportar`)
 *   - Director Comercial (solo director)
 *   - Reportes (director + `reportes.exportar` / `ventas.crear` / `comisiones.ver`)
 *
 * El tab activo se sincroniza con `?tab=` para deep-linking.
 */
export default function PedidosHubPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const canVerRemi = useHasPerm("ventas.ver_todos");
  const canAprobarPagos = useHasPerm("pagos.aprobar");
  const canRechazarPagos = useHasPerm("pagos.rechazar");
  const canVerComisiones = useHasPerm("comisiones.ver");
  const canVerReportes = useHasPerm("reportes.exportar");
  const canVender = useHasPerm("ventas.crear");

  const TABS = useMemo(() => [
    { key: "pedidos", label: "Pedidos", icon: ShoppingCart, Component: PedidosPage, testid: "tab-pedidos", visible: true },
    { key: "remisiones", label: "Remisiones (OCR)", icon: FileText, Component: RemisionesPage, testid: "tab-remisiones", visible: isDirector || canVerRemi || canVender },
    // "Pagos y Comprobantes" solo se muestra a quien tiene permiso de aprobar/rechazar pagos (o al director).
    // Los vendedores siguen reportando sus comprobantes desde el detalle del pedido — no necesitan esta tab.
    { key: "pagos", label: "Pagos y Comprobantes", icon: Wallet, Component: PagosPage, testid: "tab-pagos", visible: isDirector || canAprobarPagos || canRechazarPagos },
    { key: "director-comercial", label: "Director Comercial", icon: Sparkles, Component: DirectorPage, testid: "tab-director-comercial", visible: isDirector },
    { key: "reportes", label: "Reportes", icon: TrendingUp, Component: ReportesPage, testid: "tab-reportes", visible: isDirector || canVerReportes || canVerComisiones || canVender },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [isDirector, canVerRemi, canAprobarPagos, canRechazarPagos, canVerComisiones, canVerReportes, canVender]);

  const visibles = TABS.filter((t) => t.visible);
  const [sp, setSp] = useSearchParams();
  const requested = sp.get("tab");
  const active = visibles.find((t) => t.key === requested) ? requested : visibles[0]?.key || "pedidos";
  const Active = visibles.find((t) => t.key === active);
  const setTab = (k) => {
    const n = new URLSearchParams(sp);
    n.set("tab", k);
    setSp(n, { replace: true });
  };

  return (
    <div>
      <div className="border-b border-border mb-6">
        <div className="flex gap-1 overflow-x-auto -mb-px" role="tablist" aria-label="Secciones de Pedidos">
          {visibles.map((t) => {
            const Icon = t.icon;
            const isActive = active === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.key)}
                data-testid={t.testid}
                className={`px-4 h-11 flex items-center gap-2 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
      <div data-testid={`pedidos-tab-content-${active}`} className="hub-embedded">
        {Active && <Active.Component embedded />}
      </div>
    </div>
  );
}
